import React, {Fragment, useRef, useState} from 'react'
import type {FunctionComponent, SyntheticEvent} from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import {
  useReportItemSeen,
  useReportWidgetClick,
  useReportWidgetSeen,
  useSendPartnerProductClickout,
} from '~/hooks/analytic-hooks'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {getSchemaPriceValidUntil, objectToQueryString} from '~/utils/commons'
import {ChosenByCheckIcon} from '~/icons/product-page/chosen-by-check-icon'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {FeedbackStar} from '~/icons/product-features-page/feedback-star'
import {IconProductBrand} from '~/icons/icon-product-brand'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'

import {IconKeySpec} from '~/icons/icon-key-spec'
import {AmazonLogoIcon} from '~/icons/amazon-logo-icon'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import ProductCardBadge from '../badges/card-placement-badge'
import VariantsIndicationNewLayout from '../badges/variants-indication-new-layout'
import SaleEventBadge from '../badges/saleEventBadge'
import KeyFeaturesSpecsLayout from './key-features-specs-layout'

export interface ProductCardProps {
  index: number
  product: Product
  showProductPageLinks?: boolean
}

export const ProductCardSpecsLayout: FunctionComponent<ProductCardProps> = ({
  product,
  index: placement,
  showProductPageLinks = true,
}) => {
  const {
    image = '',
    name: initialName,
    nameTranslation,
    brand = '',
    listScore,
    affiliateLink,
    discount,
    badge,
    isSellerProduct,
    asin,
    price,
    productPageSlug,
    imageVariants,
    keySpecs,
    forcedPosition,
  } = product

  const {
    locale,
    language,
    forcedLanguage,
    featureFlags,
    trackAnalytics,
    currencyCode,
    deviceType,
    hostname,
    domain,
    renderTimestamp,
  } = useRouteLoaderData('root') as RootLoaderResponse

  const name = (forcedLanguage ? nameTranslation : initialName) ?? initialName

  const {
    dataResponse: {productsCategory} = {} as SlugApiResponse,
    clickId,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const {styles: stylesBydomain, domainName} = getDomainConfig(domain, locale)

  const [isKeyFeaturesOpen, setIsKeyFeaturesOpen] = useState(false)
  const [isKeySpecsOpen, setIsKeySpecsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const startX = useRef(0)
  const deltaX = useRef(0)

  const showVariantsIndication =
    featureFlags?.showVariantsIndication && product?.variationDetails && product?.variationDetails.length > 0

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const images = imageVariants ? [image, ...imageVariants] : [image]
  const imageVariantsByDevice = deviceType === 'desktop' ? images?.slice(0, 3) : images?.slice(0, 5)

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const scoreFormatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  const displayScore = listScore

  const reportWidgetClick = useReportWidgetClick()
  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'key-specs',
    widget_place: `card-${placement + 1}`,
  })
  const reportClickout = useSendPartnerProductClickout({
    product,
    index: placement + 1,
    locale,
    listName: 'category-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })

  const [cardRef] = useReportItemSeen({
    item_list_id: productsCategory?.slug,
    item_name: name,
    item_source: 'list',
    partner_item_brand: brand,
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: isSellerProduct,
    price: price?.amount,
    currency: price?.currency,
    click_element_name: 'category-product',
    index: placement + 1,
    variation: JSON.stringify({
      hasVariantIndication: showVariantsIndication,
      forcedPosition,
    }),
  })

  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'product-card',
      widget_place: 'list',
      variation: JSON.stringify({
        hasVariantIndication: showVariantsIndication,
        forcedPosition,
      }),
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    if (startX.current === null) return
    deltaX.current = e.touches[0].clientX - startX.current
  }
  const handleTouchEnd = () => {
    if (Math.abs(deltaX.current) > 50) {
      if (deltaX.current > 0) {
        prevSlide()
      } else {
        nextSlide()
      }
    }
    startX.current = 0
    deltaX.current = 0
  }
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % imageVariantsByDevice?.length)
  }
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? imageVariantsByDevice?.length - 1 : prevIndex - 1))
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: name,
            image: imageVariantsByDevice,
            description: name,
            brand: {
              '@type': 'Brand',
              name: brand,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: price?.amount,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: brand ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>

      <div
        ref={cardRef}
        data-testid={`product-card-${placement}`}
        className="border-gray relative z-20 flex h-full min-h-52 w-full flex-col overflow-hidden rounded-3xl border bg-white shadow transition-all duration-300 ease-in-out"
      >
        {/* -------------------------------------------------------------------- BADGES ON TOP WRAPPER -------------- */}
        <div className="badges flex flex-row gap-4 px-10 tablet:justify-between tablet:px-5">
          {/* ----------------------------------------------------------------- Product placement badge -------------- */}
          <ProductCardBadge index={placement} badge={badge} className="overflow-hidden rounded-b-lg rounded-t-none" />
          {/* --------------------------------------------------------------------------- Score -------------- */}
          <a
            href={redirectUrl}
            onClick={handleCardClick}
            aria-label={`view ${brand}`}
            target="_blank"
            rel="sponsored noreferrer"
            className="flex flex-row items-center gap-2 tablet:mt-2"
          >
            <FeedbackStar className="size-5 fill-yellow-450" />
            <span className="text-base font-normal">
              {scoreFormatter.format(displayScore)}&nbsp;
              {translations.ProductCard.scoreSuffix}
            </span>
          </a>
        </div>
        {/* ------------------------------------------------------------------------ CARD MAIN CONTENT -------------- */}
        <div className="relative mb-5 mt-2 flex h-full flex-row items-center px-8 tablet:mb-0 tablet:flex-col tablet:px-0">
          <div className="flex w-full flex-row items-center gap-5 tablet:flex-col tablet:gap-0">
            {/* --------------------------------------------------------------------------- Name Mobile ------------------------ */}
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className="hidden px-10 tablet:block tablet:px-5"
            >
              <h2 className="line-clamp-2 overflow-hidden text-base font-light leading-7 no-underline">{name}</h2>
            </a>
            {/* ------------------------------------------------------------------------- Images Courusel ----------------------- */}

            <div className="relative flex flex-col justify-center gap-4 border-r border-r-[#CBD5E1] desktop:min-w-56 desktop:pr-5 tablet:flex-col tablet:gap-6 tablet:border-none">
              <div className="flex justify-center overflow-hidden">
                <div
                  className="flex size-52 tablet:transition-transform tablet:duration-500"
                  style={{
                    transform: `translateX(-${currentIndex * 100}%)`,
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {imageVariantsByDevice?.map((image, index) => (
                    <Fragment key={`${image}-${index}`}>
                      <a
                        href={redirectUrl}
                        onClick={handleCardClick}
                        aria-label={`view ${index + 1} image of ${brand}`}
                        target="_blank"
                        rel="sponsored noreferrer"
                        className="flex size-52 flex-shrink-0 items-center justify-center"
                      >
                        <img
                          src={image}
                          alt={`Slide ${index + 1}`}
                          className="size-44 flex-shrink-0 object-scale-down"
                        />
                      </a>
                    </Fragment>
                  ))}
                </div>
              </div>

              {imageVariantsByDevice && imageVariantsByDevice.length > 1 ? (
                <div className="flex flex-row items-center justify-between tablet:justify-center tablet:space-x-5">
                  {imageVariantsByDevice?.map((image, index) => (
                    <button
                      type="button"
                      key={`${image}-${index}`}
                      onClick={() => {
                        setCurrentIndex(index)
                        reportWidgetClick({
                          widget_name: 'image-variants-carousel',
                          widget_place: `card-${placement + 1}`,
                          trigger_type: `image-${index + 1}`,
                        })
                      }}
                      className={clsx(
                        'flex flex-shrink-0 items-center justify-center rounded-sm desktop:size-14 desktop:border desktop:border-neutral-50 desktop:bg-inherit desktop:p-0.5 desktop:shadow-elegant tablet:h-[13px] tablet:w-[13px] tablet:rounded-full',
                        currentIndex === index
                          ? `${stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY} listpage_button_new_layout_primary`
                          : 'bg-[#E2E8F0]',
                      )}
                    >
                      <img alt={image} src={image} className="size-14 flex-shrink-0 object-scale-down tablet:hidden" />
                    </button>
                  ))}
                </div>
              ) : null}

              {/* --------------------------------------------------------------------------- Brand Desktop ------------------------ */}

              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className="mx-auto flex flex-row items-center gap-2 tablet:hidden"
              >
                <IconProductBrand />
                <p
                  className="line-clamp-1 text-lg font-bold capitalize tablet:text-base tablet:font-medium"
                  data-testid="category-card-brand"
                >
                  {brand}
                </p>
              </a>
            </div>

            {/* --------------------------------------------------------------------------- Brand Mobile------------------------ */}

            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className="mr-auto hidden flex-row items-center gap-2 px-5 tablet:my-2 tablet:flex"
            >
              <IconProductBrand />
              <p
                className="line-clamp-1 text-lg font-bold capitalize tablet:text-base tablet:font-medium"
                data-testid="category-card-brand"
              >
                {brand}
              </p>
            </a>

            {showVariantsIndication && (
              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className="mr-auto hidden px-10 tablet:my-2 tablet:block tablet:px-5"
              >
                <VariantsIndicationNewLayout
                  className="line-clamp-1 flex w-full flex-row gap-1"
                  index={placement + 1}
                  variations={product?.variationDetails}
                  reportVariantsClicked={() => {
                    reportWidgetClick({
                      widget_name: 'variants-indication',
                      widget_place: `card-${placement + 1}`,
                      trigger_type: 'click',
                      variation: 'product-card',
                    })
                  }}
                />
              </a>
            )}
            {/* --------------------------------------------------------------------------- INFO ------------------------ */}

            <div className="flex h-full w-full flex-col gap-5">
              {/* --------------------------------------------------------------------------- Name Desktop ------------------------ */}
              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className="tablet:hidden"
              >
                <h2 className="line-clamp-2 overflow-hidden text-lg font-semibold leading-7 no-underline">{name}</h2>
              </a>
              <div className="flex flex-row items-center justify-center tablet:flex-col">
                {/* --------------------------------------------------------------------------- Specs Desktop ------------------------ */}
                {keySpecs && !!keySpecs?.length ? (
                  <div
                    ref={widgetRef}
                    className={clsx(
                      'flex flex-col gap-2 overflow-hidden border-t-[#D4D4D8] transition-all duration-200 ease-in-out tablet:my-3 tablet:w-full tablet:border-t tablet:px-5 tablet:pt-4',
                      isKeySpecsOpen && 'border-b-gray-300 tablet:border-b tablet:pb-3',
                    )}
                  >
                    <div
                      className={clsx(
                        'flex flex-col gap-1 overflow-hidden',
                        isKeySpecsOpen ? 'tablet:max-h-full' : 'tablet:max-h-[70px]',
                      )}
                    >
                      {keySpecs.map(({name, value}, index) => (
                        <Fragment key={`${name}-${index}`}>
                          <div className="flex flex-row items-start gap-1 text-base tablet:text-sm">
                            <IconKeySpec
                              className={clsx(
                                'mt-1 size-4 shrink-0 tablet:mt-0.5',
                                stylesBydomain.LISTPAGE_WIDGETS_SPECS_CHECKED_ICON,
                                'listpage_widgets_specs_checked_icon',
                              )}
                            />
                            <span className="font-semibold">
                              {name}: <em className="font-light not-italic">{value}</em>
                            </span>
                          </div>
                        </Fragment>
                      ))}
                    </div>
                    <button
                      type="button"
                      aria-label="toggle expand key specs"
                      className={clsx(
                        'hidden w-full items-center justify-center rounded-md py-1 tablet:flex',
                        stylesBydomain.LISTPAGE_WIDGETS_SPECS_ACCORDION_BUTTON_BG,
                        stylesBydomain.LISTPAGE_WIDGETS_SPECS_ACCORDION_BUTTON_ICON,
                        'listpage_widgets_specs_accordion_button_bg listpage_widgets_specs_accordion_button_icon',
                      )}
                      onClick={() => {
                        reportWidgetClick({
                          widget_name: 'key-specs',
                          widget_place: `card-${placement + 1}`,
                          trigger_type: isKeySpecsOpen ? 'close' : 'open',
                        })
                        setIsKeySpecsOpen((prev) => !prev)
                      }}
                    >
                      <IconChevronRightThick
                        className={clsx(
                          'size-5 transition-all duration-100 ease-in-out',
                          isKeySpecsOpen ? '-rotate-90' : 'rotate-90',
                        )}
                      />
                    </button>
                  </div>
                ) : null}

                {/* --------------------------------------------------------------------------------- CTA ------------------------- */}
                <div className="ml-auto flex h-full w-full max-w-[334px] flex-shrink-0 flex-col items-center justify-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-xl tablet:max-w-full tablet:gap-1 tablet:rounded-none tablet:bg-[#F1F5F9] tablet:shadow-none">
                  {/* --------------------------------------------------------------------- MARKETPLACEs CTA WRAPPER ----------------------- */}
                  <div className="flex w-full flex-col gap-3">
                    {/* -------------------------------------------------------------------------- Amazon cta -------------------------------- */}
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                      className={clsx(
                        'relative flex w-full flex-row justify-start gap-4 rounded-3xl p-2.5',
                        stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY,
                        'listpage_button_new_layout_primary',
                      )}
                    >
                      <div
                        className={clsx(
                          'absolute top-2.5 flex shrink-0 items-center justify-center rounded-2xl px-2 py-0.5',
                          stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_BG,
                          stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_ICON,
                          stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_TEXT,
                          'listpage_badge_cta_marketplace_logo_bg listpage_badge_cta_marketplace_logo_text listpage_badge_cta_marketplace_logo_icon',
                        )}
                      >
                        <AmazonLogoIcon
                          className={clsx(
                            'mt-1 h-4 shrink-0',
                            domainName === 'bestestaubsauger' ||
                              (domainName === 'bestexpertschoice' && locale.toLowerCase() === 'ca')
                              ? 'w-14'
                              : 'w-11',
                          )}
                          domainName={domainName}
                        />
                      </div>
                      <p className="mx-auto text-base font-semibold capitalize">
                        {translations.ProductCard.checkPrice}
                      </p>
                      {discount ? (
                        <div
                          className={clsx(
                            'absolute right-2.5 top-2.5 ml-auto flex items-center justify-center rounded-2xl px-2 py-1 text-xs lowercase',
                            stylesBydomain.LISTPAGE_BADGE_CTA_DISCOUNT_BG,
                            stylesBydomain.LISTPAGE_BADGE_CTA_DISCOUNT_TEXT,
                            'listpage_badge_cta_discount_bg listpage_badge_cta_discount_text',
                          )}
                        >
                          {translations.Badge.discountAmount(discount)}&nbsp;
                          {translations.Badge.off}
                        </div>
                      ) : null}
                    </a>
                  </div>

                  {product.chosenBy && placement === 0 ? (
                    <div className="mx-auto mt-3 flex flex-row items-center gap-1">
                      <ChosenByCheckIcon className="hidden tablet:block" />
                      <p className="text-sm font-light text-[#64748B]">
                        {translations.Badge.chosenBy(product.chosenBy)}
                      </p>
                    </div>
                  ) : null}
                  {!!discount && (
                    <SaleEventBadge
                      redirectUrl={redirectUrl}
                      handleCardClick={handleCardClick}
                      brand={brand}
                      index={placement}
                      placement="spec-card"
                    />
                  )}
                </div>
              </div>

              {/* --------------------------------------------------------------------------- Variants Desktop ------------------------ */}
              {showVariantsIndication && (
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                  className="rounded-md border border-[#CBD5E1] bg-[#F8FAFC] px-2 py-1.5 tablet:hidden"
                >
                  <VariantsIndicationNewLayout
                    className="line-clamp-1 flex w-full flex-row gap-1"
                    index={placement + 1}
                    variations={product?.variationDetails}
                    reportVariantsClicked={() => {
                      reportWidgetClick({
                        widget_name: 'variants-indication',
                        widget_place: `card-${placement + 1}`,
                        trigger_type: 'click',
                        variation: 'product-card',
                      })
                    }}
                  />
                </a>
              )}
            </div>
          </div>
        </div>
        {/* ------------------------------------------------------------------------ KEY FEATURES -------------- */}
        <KeyFeaturesSpecsLayout
          placement={placement + 1}
          productPageSlug={productPageSlug ?? ''}
          showProductPageLinks={showProductPageLinks}
          handleCardClick={handleCardClick}
          product={product}
          affiliateLinkWithClickId={affiliateLinkWithClickId}
          isKeyFeaturesOpen={isKeyFeaturesOpen}
          setIsKeyFeaturesOpen={setIsKeyFeaturesOpen}
        />
      </div>
    </>
  )
}
